import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { api, ApiError } from './api.js';
import type { Me } from './api.js';
import { Help } from './components/Help.js';
import { Account } from './pages/Account.js';
import { Feedback } from './pages/Feedback.js';
import { SignIn } from './pages/SignIn.js';
import { ResetPassword } from './pages/ResetPassword.js';
import { VerifyEmail } from './pages/VerifyEmail.js';
import { Lobby } from './pages/Lobby.js';
import { GameLobby } from './pages/GameLobby.js';
import { Game } from './pages/Game.js';
import { NewGame } from './pages/NewGame.js';
import { Replay } from './pages/Replay.js';

interface Session {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session>({
  me: null, loading: true,
  refresh: async () => undefined,
  signOut: async () => undefined,
});

export const useSession = () => useContext(SessionContext);

/**
 * Settings of the game currently on screen, for the parts of the shell
 * that sit OUTSIDE the routes and still need them.
 *
 * Two parts need them: the rules summary in the masthead, which hides
 * its debris note when the host switched debris off and shows the
 * turn-mode sequence in a turn-mode game, and the masthead's
 * list of the players at the table. The masthead is a sibling of the
 * routes, so the game page cannot pass it down as a prop; it publishes
 * here instead.
 *
 * `null` means no game is on screen -- the lobby, the course editor --
 * and every consumer must read that as "no opinion" rather than as off.
 */
interface CurrentGame {
  debris: boolean;
  turnMode: boolean;
  /** The table in seat order, each with its robot's colour. */
  players: { name: string; colour: string }[];
}

/** How much of a name the masthead has room for. */
const MASTHEAD_NAME = 7;

const CurrentGameContext = createContext<{
  game: CurrentGame | null;
  setGame: (game: CurrentGame | null) => void;
}>({ game: null, setGame: () => undefined });

export const useCurrentGame = () => useContext(CurrentGameContext);

function SessionProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me());
    } catch (err) {
      // A 401 here is the ordinary "not signed in" case, not a failure.
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.logout();
    setMe(null);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <SessionContext.Provider value={{ me, loading, refresh, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

function Masthead() {
  const { me, signOut } = useSession();
  const { game } = useCurrentGame();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  if (!me) return null;

  return (
    <>
      <header className="masthead">
        <span className="wordmark">RallyX</span>
        {/* The table, one line, each name cut to fit and in its robot's
            colour. The full name is the tooltip. */}
        {game && game.players.length > 0 && (
          <div className="masthead-table mono">
            {game.players.map((p, i) => (
              <span key={i} style={{ color: p.colour }} title={p.name}>
                {Array.from(p.name).slice(0, MASTHEAD_NAME).join('')}
              </span>
            ))}
          </div>
        )}
        <div className="row">
          <button
            className="help-button"
            onClick={() => setHelpOpen(true)}
            aria-label="Rules summary"
            title="Rules summary"
          >
            ?
          </button>
          {/* Beside the rules, because both are the same kind of
              thing: the two buttons that are about the site rather
              than about the game on screen. */}
          <button
            className="secondary"
            onClick={() => setFeedbackOpen(true)}
            title="Tell the site admin something"
          >
            Feedback
          </button>
          {/* The name is the way into the account panel: there is nowhere
              else it would sensibly live, and a separate button would
              crowd the masthead. */}
          <button
            className="secondary name-button"
            onClick={() => setAccountOpen(true)}
            title="Account"
          >
            {me.username}
          </button>
          <button
            className="secondary"
            onClick={async () => { await signOut(); navigate('/sign-in'); }}
          >
            Sign out
          </button>
        </div>
      </header>
      <div className="hazard hazard-thin" role="presentation" />

      {helpOpen && (
        <Help
          onClose={() => setHelpOpen(false)}
          debris={game ? game.debris : null}
          turnMode={game ? game.turnMode : null}
        />
      )}
      {feedbackOpen && (
        <Feedback username={me.username} onClose={() => setFeedbackOpen(false)} />
      )}
      {accountOpen && (
        <Account username={me.username} onClose={() => setAccountOpen(false)} />
      )}
    </>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const { me, loading } = useSession();
  if (loading) {
    return <div className="content"><p className="eyebrow">Loading</p></div>;
  }
  return me ? <>{children}</> : <Navigate to="/sign-in" replace />;
}

function CurrentGameProvider({ children }: { children: React.ReactNode }) {
  const [game, setGame] = useState<CurrentGame | null>(null);
  return (
    <CurrentGameContext.Provider value={{ game, setGame }}>
      {children}
    </CurrentGameContext.Provider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <CurrentGameProvider>
        <div className="shell">
          <Masthead />
          <Routes>
            <Route path="/sign-in" element={<SignIn />} />
            {/* Opened from an emailed link, so it needs no session:
                someone who cannot sign in is exactly who it is for. */}
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route
              path="/"
              element={<RequireSession><Lobby /></RequireSession>}
            />
            <Route
              path="/new"
              element={<RequireSession><NewGame /></RequireSession>}
            />
            <Route
              path="/games/:gameId"
              element={<RequireSession><GameLobby /></RequireSession>}
            />
            <Route
              path="/games/:gameId/play"
              element={<RequireSession><Game /></RequireSession>}
            />
            {/* Its own route rather than a mode of the board: replay
                shares the board and the log panel with a live game and
                none of the machinery around them. */}
            <Route
              path="/games/:gameId/replay"
              element={<RequireSession><Replay /></RequireSession>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Which build this is. Small, fixed, and out of the way, but
              always readable: a stale bundle is otherwise invisible and
              has already been mistaken for several bugs. */}
          <div
            className="build-stamp"
            title="Client build time (US Eastern), commit and branch; + is uncommitted changes"
          >
            {__BUILD_STAMP__}{__BUILD_COMMIT__ && ` ${__BUILD_COMMIT__}`}
          </div>
        </div>
        </CurrentGameProvider>
      </SessionProvider>
    </BrowserRouter>
  );
}
